/**
 * ServiceAccessPointsTab — tab within ServiceForm showing all access points
 * for a service, with create/edit/delete capabilities.
 *
 * Each access point can be:
 *  - URL-based (simple web link)
 *  - CI-based (linked to any CMDB entity via ci_type/ci_id)
 *  - Both (e.g., URL to admin panel + CI reference to the server)
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  GlobeAltIcon,
  ServerStackIcon,
  KeyIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import CiSelector from './CiSelector';

// Protocolos de acceso (desplegable) y puertos por defecto de cada uno.
const ACCESS_PROTOCOLS = ['https', 'http', 'rdp', 'ssh', 'vnc', 'telnet', 'ftp', 'custom'];
const PROTOCOL_PORTS = { https: 443, http: 80, rdp: 3389, ssh: 22, vnc: 5900, telnet: 23, ftp: 21 };

// Protocolo sugerido según el tipo de acceso elegido.
const ACCESS_TYPE_PROTOCOL = {
  https: 'https', http: 'http', rdp: 'rdp', ssh: 'ssh', terminal_server: 'rdp',
  api: 'https', database: 'tcp', console: 'vnc', vpn: 'https', telnet: 'telnet',
  snmp: 'udp', ipmi: 'https', rdp_gateway: 'rdp', vnc: 'vnc', other: 'custom',
};

// Si la URL no lleva esquema, se lo añadimos según el protocolo elegido.
function ensureUrlScheme(url, protocol) {
  const u = (url || '').trim();
  if (!u) return u;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(u)) return u;
  const p = protocol === 'custom' ? 'https' : (protocol || 'https');
  return `${p}://${u}`;
}

const ACCESS_ICONS = {
  ssh:              '🔒',
  rdp:              '🖥️',
  https:            '🌐',
  http:             '🌐',
  terminal_server:  '🖥️',
  api:              '🔌',
  database:         '🗄️',
  console:          '🖥️',
  vpn:              '🔐',
  telnet:           '🔌',
  snmp:             '📡',
  ipmi:             '⚙️',
  rdp_gateway:      '🖥️',
  vnc:              '🖥️',
  other:            '🔗',
};

const SCOPE_COLORS = {
  user:           { bg: 'var(--green-50)', color: 'var(--green-600)', label: 'Usuario' },
  admin:          { bg: 'var(--amber-50)', color: 'var(--amber-600)', label: 'Administración' },
  service:        { bg: 'var(--blue-50)', color: 'var(--blue-700)', label: 'Servicio' },
  infrastructure: { bg: 'var(--bg-toolbar)', color: 'var(--text-secondary)', label: 'Infraestructura' },
};

// L6 Passwords: el acceso se queda en CMDB; el secreto vive en el vault.
const PASSWORDS_URL = (process.env.REACT_APP_PASSWORDS_URL || 'http://localhost:5179').replace(/\/$/, '');

function passwordsServiceLinkUrl(ap, serviceId) {
  const meta = {
    accessType: ap.access_type,
    accessTypeLabel: ap.access_type_label || ap.access_type,
    url: ap.url || null,
    ci: ap.ci_type ? `${ap.ci_type} #${ap.ci_id}` : null,
    port: ap.port ?? null,
    protocol: ap.protocol || null,
  };
  const params = new URLSearchParams();
  params.set('link', `cmdb:service_access_point:${ap.id}`);
  params.set('label', `${ap.name} · servicio ${serviceId}`);
  params.set('meta', JSON.stringify(meta));
  return `${PASSWORDS_URL}/passwords/vault?${params.toString()}`;
}

function passwordsServiceResourceUrl(ap) {
  const params = new URLSearchParams();
  params.set('resource', `cmdb:service_access_point:${ap.id}`);
  return `${PASSWORDS_URL}/passwords/vault?${params.toString()}`;
}

export default function ServiceAccessPointsTab({ serviceId, companyId, onCountChange }) {
  const [points, setPoints] = useState([]);
  const [meta, setMeta] = useState({ access_types: [], scopes: [], ci_types: [] });
  const [credStatus, setCredStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', access_type: 'https', scope: 'service', url: '',
    ci_type: '', ci_id: '', port: '', protocol: 'https',
    credentials_ref: '', is_primary: false, notes: '',
  });

  const load = useCallback(async () => {
    if (!serviceId || !companyId) return;
    setLoading(true);
    setError(null);
    try {
      const [res, m] = await Promise.all([
        api.getServiceAccessPoints(serviceId, companyId),
        api.getServiceAccessPointsMeta(companyId),
      ]);
      setPoints(res.access_points || []);
      if (m.access_types) setMeta(m);
      onCountChange?.(res.access_points?.length || 0);
      // Insignia de credenciales: solo recuentos, nunca el secreto.
      const lista = res.access_points || [];
      if (lista.length) {
        api.getCredentialStatus(
          lista.map(ap => `service_access_point:${ap.id}`),
          companyId
        ).then(setCredStatus).catch(() => setCredStatus({}));
      } else {
        setCredStatus({});
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [serviceId, companyId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({
      name: '', access_type: 'https', scope: 'service', url: '',
      ci_type: '', ci_id: '', port: '', protocol: 'https',
      credentials_ref: '', is_primary: false, notes: '',
    });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (ap) => {
    setForm({
      name: ap.name || '',
      access_type: ap.access_type || 'https',
      scope: ap.scope || 'service',
      url: ap.url || '',
      ci_type: ap.ci_type || '',
      ci_id: ap.ci_id || '',
      port: ap.port || '',
      protocol: ap.protocol || 'tcp',
      credentials_ref: ap.credentials_ref || '',
      is_primary: ap.is_primary || false,
      notes: ap.notes || '',
    });
    setEditing(ap);
    setShowForm(true);
  };

  const handleAccessTypeChange = (val) => {
    // Al cambiar el tipo de acceso, sugerimos el protocolo por defecto.
    setForm(f => ({ ...f, access_type: val, protocol: ACCESS_TYPE_PROTOCOL[val] || f.protocol }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form };
      if (payload.port === '') payload.port = null;
      else payload.port = Number(payload.port);
      if (payload.ci_id === '') payload.ci_id = null;
      else if (payload.ci_id) payload.ci_id = Number(payload.ci_id);
      // Traducir host/IP → URL con protocolo (y puerto si no es el normal).
      const rawUrl = (payload.url || '').trim();
      payload.url = ensureUrlScheme(rawUrl, payload.protocol);
      const defPort = PROTOCOL_PORTS[payload.protocol];
      if (rawUrl && payload.port && !/:[0-9]+$/.test(payload.url) && Number(payload.port) !== (defPort || 0)) {
        payload.url = `${payload.url}:${payload.port}`;
      }

      if (editing) {
        await api.updateServiceAccessPoint(serviceId, editing.id, payload, companyId);
      } else {
        await api.createServiceAccessPoint(serviceId, payload, companyId);
      }
      resetForm();
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ap) => {
    if (!window.confirm(`¿Eliminar el acceso "${ap.name}"?`)) return;
    try {
      await api.deleteServiceAccessPoint(serviceId, ap.id, companyId);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleCiSelect = (ci) => {
    setForm(prev => ({ ...prev, ci_type: ci.ci_type, ci_id: ci.ci_id }));
  };

  const totalAccessPoints = points.length;
  const primaryCount = points.filter(p => p.is_primary).length;

  return (
    <div>
      {/* Summary */}
      <div className="dv-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="dv-section-title" style={{ margin: 0, border: 'none', paddingBottom: 0 }}>
            Puntos de acceso ({totalAccessPoints})
          </div>
          <button
            type="button"
            className="dv-btn dv-btn-primary"
            onClick={() => { resetForm(); setShowForm(true); }}
            style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}
            disabled={showForm}
          >
            <PlusIcon style={{ width: 14, height: 14 }} />
            Añadir acceso
          </button>
        </div>
        {totalAccessPoints > 0 && (
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.78rem', marginBottom: 8 }}>
            {primaryCount} primario{primaryCount !== 1 ? 's' : ''} · {totalAccessPoints} total
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger py-2" style={{ fontSize: '0.82rem' }} role="alert">
          ⚠ {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: '0.85rem' }}>Cargando accesos…</div>
      )}

      {/* Add/Edit Form */}
      {showForm && !loading && (
        <div style={{
          background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 8,
          padding: 16, marginBottom: 16,
        }}>
          <div className="dv-section-title" style={{ margin: '0 0 12px 0', border: 'none', padding: 0, fontSize: '0.95rem' }}>
            {editing ? 'Editar acceso' : 'Nuevo acceso'}
          </div>
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Nombre *</label>
              <input
                className="form-control form-control-sm"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Panel admin, SSH nodo 1…"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Tipo de acceso</label>
              <select
                className="form-select form-select-sm"
                value={form.access_type}
                onChange={e => handleAccessTypeChange(e.target.value)}
              >
                {(meta.access_types || []).map(t => (
                  <option key={t.value} value={t.value}>
                    {ACCESS_ICONS[t.value] || '🔗'} {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Ámbito</label>
              <select
                className="form-select form-select-sm"
                value={form.scope}
                onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
              >
                {(meta.scopes || []).map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>URL (host/IP)</label>
              <input
                className="form-control form-control-sm"
                value={form.url}
                onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                placeholder="10.0.0.5 / panel.empresa.com"
              />
              {form.url && (
                <div className="form-text" style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {ensureUrlScheme(form.url, form.protocol)}
                </div>
              )}
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Protocolo</label>
              <select
                className="form-select form-select-sm"
                value={form.protocol}
                onChange={e => setForm(f => ({ ...f, protocol: e.target.value }))}
              >
                {ACCESS_PROTOCOLS.map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>CI asociado (opcional)</label>
              <CiSelector
                ciType={form.ci_type}
                ciId={form.ci_id}
                companyId={companyId}
                onSelect={handleCiSelect}
                onClear={() => setForm(f => ({ ...f, ci_type: '', ci_id: '' }))}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Puerto</label>
              <input
                type="number"
                className="form-control form-control-sm"
                value={form.port}
                onChange={e => setForm(f => ({ ...f, port: e.target.value }))}
                placeholder="443"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Enlace a Odoo (credenciales)</label>
              <input
                className="form-control form-control-sm"
                value={form.credentials_ref}
                onChange={e => setForm(f => ({ ...f, credentials_ref: e.target.value }))}
                placeholder="https://odoo.empresa.com/web#id=… / clave…"
              />
            </div>
            <div className="col-md-12">
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Notas</label>
              <input
                className="form-control form-control-sm"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notas adicionales sobre este acceso…"
              />
            </div>
            <div className="col-md-4" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 24 }}>
              <label style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="checkbox"
                  checked={form.is_primary}
                  onChange={e => setForm(f => ({ ...f, is_primary: e.target.checked }))}
                />
                Acceso principal
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              className="dv-btn dv-btn-primary"
              onClick={handleSave}
              disabled={saving}
              style={{ fontSize: '0.78rem' }}
            >
              {saving ? 'Guardando…' : (editing ? 'Actualizar' : 'Crear acceso')}
            </button>
            <button
              type="button"
              className="dv-btn dv-btn-secondary"
              onClick={resetForm}
              style={{ fontSize: '0.78rem' }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Access points list */}
      {!loading && totalAccessPoints === 0 && !showForm && (
        <div className="dv-empty-state">
          <span style={{ fontSize: '1.5rem' }}>🔗</span>
          <p style={{ marginTop: 8 }}>Sin accesos definidos</p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
            Añade URLs, SSH, RDP y otros accesos a este servicio.
          </p>
        </div>
      )}

      {!loading && totalAccessPoints > 0 && (
        <table className="dv-servers-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Tipo</th>
              <th>Ámbito</th>
              <th>Destino</th>
              <th>Credenciales</th>
              <th style={{ textAlign: 'center' }}>Primario</th>
              <th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {points.map(ap => {
              const icon = ACCESS_ICONS[ap.access_type] || '🔗';
              const scopeStyle = SCOPE_COLORS[ap.scope] || SCOPE_COLORS.service;
              return (
                <tr key={ap.id}>
                  <td style={{ fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>{icon}</span>
                      <span>{ap.name}</span>
                    </div>
                    {ap.notes && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>{ap.notes}</div>
                    )}
                  </td>
                  <td>{ap.access_type_label || ap.access_type}</td>
                  <td>
                    <span style={{
                      display: 'inline-block', padding: '1px 6px', borderRadius: 8,
                      fontSize: '0.7rem', fontWeight: 600,
                      background: scopeStyle.bg, color: scopeStyle.color,
                    }}>
                      {scopeStyle.label}
                    </span>
                  </td>
                  <td>
                    {ap.url && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <GlobeAltIcon style={{ width: 12, height: 12, color: 'var(--text-secondary)' }} />
                        <a href={ap.url} target="_blank" rel="noopener noreferrer"
                           style={{ fontSize: '0.82rem', color: 'var(--accent)', textDecoration: 'none' }}
                           onClick={e => e.stopPropagation()}>
                          {ap.url.replace(/^https?:\/\//, '').substring(0, 30)}
                          <ArrowTopRightOnSquareIcon style={{ width: 10, height: 10, marginLeft: 2, display: 'inline' }} />
                        </a>
                      </div>
                    )}
                    {ap.ci_type && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                        <ServerStackIcon style={{ width: 12, height: 12 }} />
                        {ap.ci_type_label || ap.ci_type} #{ap.ci_id}
                      </div>
                    )}
                    {ap.port && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                        :{ap.port}/{ap.protocol || 'tcp'}
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 4, whiteSpace: 'nowrap', alignItems: 'center' }}>
                      {(() => {
                        const status = credStatus[`service_access_point:${ap.id}`];
                        if (!status || !status.count) return null;
                        const tipos = (status.accessTypes || []).join(', ');
                        return (
                          <a
                            href={passwordsServiceResourceUrl(ap)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`${status.count} credencial(es) en L6 Passwords${tipos ? ` (${tipos})` : ''}`}
                            style={{
                              padding: '1px 7px',
                              borderRadius: 10,
                              fontSize: '0.74rem',
                              fontWeight: 600,
                              background: 'rgba(34,197,94,0.14)',
                              color: '#15803d',
                              textDecoration: 'none',
                            }}
                            onClick={e => e.stopPropagation()}
                          >
                            🔑 {status.count}{status.hasPrimary ? ' ★' : ''}
                          </a>
                        );
                      })()}
                      <a
                        href={passwordsServiceLinkUrl(ap, serviceId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Vincular una credencial de L6 Passwords a este acceso"
                        style={{ fontSize: '0.78rem', color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}
                        onClick={e => e.stopPropagation()}
                      >
                        {credStatus[`service_access_point:${ap.id}`]?.count ? 'Añadir' : '🔑 Vincular'}
                      </a>
                      <a
                        href={passwordsServiceResourceUrl(ap)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Ver credenciales vinculadas en L6 Passwords"
                        style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', textDecoration: 'none' }}
                        onClick={e => e.stopPropagation()}
                      >
                        Ver
                      </a>
                    </div>
                    {ap.credentials_ref ? (
                      /^https?:\/\//i.test(ap.credentials_ref) ? (
                        <a href={ap.credentials_ref} target="_blank" rel="noopener noreferrer"
                           style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--accent)', textDecoration: 'none' }}
                           onClick={e => e.stopPropagation()}>
                          <KeyIcon style={{ width: 12, height: 12 }} />
                          {ap.credentials_ref.replace(/^https?:\/\//, '').substring(0, 30)}
                          <ArrowTopRightOnSquareIcon style={{ width: 10, height: 10 }} />
                        </a>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                          <KeyIcon style={{ width: 12, height: 12 }} />
                          {ap.credentials_ref}
                        </div>
                      )
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>—</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {ap.is_primary ? (
                      <span style={{ color: 'var(--green-600)', fontWeight: 600, fontSize: '0.85rem' }}>★</span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }}>☆</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="wol-action-btn"
                      onClick={() => handleEdit(ap)}
                      title="Editar"
                      style={{ marginRight: 4 }}
                    >
                      <PencilSquareIcon style={{ width: 14, height: 14 }} />
                    </button>
                    <button
                      type="button"
                      className="wol-action-btn wol-action-btn--delete"
                      onClick={() => handleDelete(ap)}
                      title="Eliminar"
                    >
                      <TrashIcon style={{ width: 14, height: 14 }} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
